import { useCallback, useEffect, useState } from "react";
import { workspaceHumanAgentSettingsClient } from "@/api/workspaceHumanAgentSettingsClient";
import { mapNotificationsFromApi, mapNotificationsToApi } from "@/api/workspaceHumanAgentSettingsMappers";
import { setInboxAlertPrefs } from "@/lib/inboxMessageAlerts";

function applyInboxPrefs(settings) {
  setInboxAlertPrefs({
    desktopEnabled: settings.desktopNotif !== false,
    soundEnabled: settings.soundNotif !== false,
    inAppEnabled: true,
  });
}

function Toggle({ on, onToggle, id, locked = false }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-disabled={locked || undefined}
      id={id}
      onClick={locked ? undefined : onToggle}
      className={`inline-flex h-7 w-12 shrink-0 items-center rounded-full p-1 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2 ${
        locked ? "cursor-default" : "cursor-pointer"
      } ${on ? "ui-toggle-track-on" : "ui-toggle-track-off"}`}
    >
      <span
        className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-md ring-1 ring-slate-200/70 transition-transform duration-200 ease-out dark:bg-slate-200 dark:ring-slate-500 ${
          on ? "translate-x-5" : "translate-x-0"
        }`}
      />
    </button>
  );
}

function SectionShell({ title, description, width = "max-w-6xl", actions = null, children }) {
  return (
    <div className={`mx-auto w-full ${width} space-y-6`}>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h3 className="text-xl font-semibold tracking-tight text-slate-900">{title}</h3>
          {description && <p className="mt-1 text-sm text-slate-500">{description}</p>}
        </div>
        {actions}
      </div>
      {children}
    </div>
  );
}

function SectionCard({ children, className = "" }) {
  return <div className={`ui-card-static !p-6 sm:!p-8 ${className}`}>{children}</div>;
}

export default function NotificationsSettingsSection() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [banner, setBanner] = useState(null);
  const [toggles, setToggles] = useState({
    emailNotif: true,
    desktopNotif: true,
    soundNotif: true,
    inAppNotif: true,
  });

  const load = useCallback(async () => {
    setLoading(true);
    setBanner(null);
    try {
      const raw = await workspaceHumanAgentSettingsClient.getNotifications();
      const mapped = { ...mapNotificationsFromApi(raw), inAppNotif: true };
      setToggles(mapped);
      applyInboxPrefs(mapped);
    } catch (e) {
      setBanner({ type: "err", text: e instanceof Error ? e.message : "Could not load notification settings." });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const toggle = (key) => setToggles((p) => ({ ...p, [key]: !p[key] }));

  const save = async () => {
    setSaving(true);
    setBanner(null);
    try {
      await workspaceHumanAgentSettingsClient.putNotifications(mapNotificationsToApi(toggles));
      applyInboxPrefs(toggles);
      setBanner({ type: "ok", text: "Notification settings saved." });
    } catch (e) {
      setBanner({ type: "err", text: e instanceof Error ? e.message : "Could not save." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <SectionShell title="Notifications" description="Choose how interruptions and updates reach your team.">
      <SectionCard className="space-y-6">
        {banner && (
          <div
            className={`rounded-lg px-3 py-2 text-sm font-medium ${
              banner.type === "ok"
                ? "bg-emerald-50 text-emerald-900 ring-1 ring-emerald-200/70"
                : "bg-rose-50 text-rose-900 ring-1 ring-rose-200/70"
            }`}
            role="status"
          >
            {banner.text}
          </div>
        )}
        {loading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : (
          <>
            {[
              { key: "emailNotif", label: "Email", desc: "Digest and real-time alerts." },
              {
                key: "inAppNotif",
                label: "In-app alerts",
                desc: "Toast inside the dashboard. Always on — this is how agents are notified while signed in.",
              },
              { key: "desktopNotif", label: "Desktop push", desc: "Browser notifications while signed in." },
              { key: "soundNotif", label: "Sound", desc: "Ping on every new customer message and handoff." },
            ].map((item) => (
              <div
                key={item.key}
                className="flex items-center justify-between gap-4 border-b border-slate-100 pb-5 last:border-0 last:pb-0"
              >
                <div>
                  <p className="text-sm font-semibold text-slate-900">{item.label}</p>
                  <p className="mt-1 text-xs text-slate-500">{item.desc}</p>
                </div>
                <Toggle
                  id={`n-${item.key}`}
                  locked={item.key === "inAppNotif"}
                  on={item.key === "inAppNotif" ? true : toggles[item.key]}
                  onToggle={() => toggle(item.key)}
                />
              </div>
            ))}
            <div className="border-t border-slate-100 pt-5">
              <button type="button" className="ui-btn-primary" disabled={saving} onClick={() => void save()}>
                {saving ? "Saving…" : "Save notification settings"}
              </button>
            </div>
          </>
        )}
      </SectionCard>
    </SectionShell>
  );
}
